import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import {
  Banner,
  Button,
  Dialog,
  ImageSlot,
  Skeleton,
  TextField,
  Textarea,
  useToast,
} from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { LockedPage } from '../../../shell/ModulePages';
import {
  completeCase,
  logCase,
  patchCase,
  remindCase,
  useAfterSalesCase,
  useInvalidateTickets,
} from '../api';
import type { AfterSalesRecord } from '../types';
import { LoadError, isForbidden } from '../ui/LoadError';
import { TicketPill } from '../ui/TicketPill';
import page from '../ui/page.module.css';
import { AssignExternalDialog } from './AssignExternalDialog';
import { ExtendDialog } from './ExtendDialog';
import { ScheduleDialog } from './ScheduleDialog';
import styles from './after-sales.module.css';

type DialogKind = 'assign-external' | 'schedule' | 'extend' | 'complete';

/**
 * `/tickets/after-sales/:id` — After-Sales 04: header with „Frist verlängern" / „Als erledigt markieren" (and the
 * status steps without frame: „An Handwerker geben …", „Termin eintragen …"), the deadline banner, „Verlauf" with the
 * report's photos and a free entry, „Zugewiesen" (Erinnerung senden, Anrufen) and „Kunde sieht im Portal".
 */
export function CasePage() {
  const { id = '' } = useParams();
  const permissions = usePermissions();
  const { toast } = useToast();
  const invalidate = useInvalidateTickets();
  const record = useAfterSalesCase(id);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reminding, setReminding] = useState(false);

  if (permissions.tool('tickets') !== 'ok' || isForbidden(record.error)) return <LockedPage tool="tickets" />;

  const data = record.data;
  if (!data) {
    return (
      <div className={`${page.page} ${page.tight}`}>
        <Crumbs number={null} />
        {record.isError ? (
          <LoadError
            what="Der Vorgang konnte nicht geladen werden"
            error={record.error}
            retrying={record.isFetching}
            onRetry={() => void record.refetch()}
          />
        ) : (
          <div className={page.skeleton} aria-busy="true">
            <Skeleton width={480} height={34} />
            <div className={styles.grid}>
              <Skeleton variant="block" height={360} />
              <Skeleton variant="block" height={300} />
            </div>
          </div>
        )}
      </div>
    );
  }

  async function remind(current: AfterSalesRecord) {
    setReminding(true);
    setNotice(null);
    try {
      await remindCase(current.id);
      await invalidate();
      toast({ message: 'Die Erinnerung ist unterwegs.' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setNotice(error.message);
      else toast({ message: errorMessage(error) });
    } finally {
      setReminding(false);
    }
  }

  const { actions } = data;
  return (
    <div className={`${page.page} ${page.tight}`}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <Crumbs number={data.numberLabel} />
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{data.title}</h1>
            <TicketPill label={data.pill.label} tone={data.pill.tone} />
          </div>
          <div className={styles.subline}>{data.subline}</div>
        </div>
        <div className={styles.headActions}>
          {actions.assignExternal ? (
            <Button variant="secondary" onClick={() => setDialog('assign-external')}>
              An Handwerker geben …
            </Button>
          ) : null}
          {actions.schedule ? (
            <Button variant="secondary" onClick={() => setDialog('schedule')}>
              Termin eintragen …
            </Button>
          ) : null}
          {actions.extend ? (
            <Button variant="secondary" onClick={() => setDialog('extend')}>
              Frist verlängern
            </Button>
          ) : null}
          {actions.complete ? (
            <Button onClick={() => setDialog('complete')}>Als erledigt markieren</Button>
          ) : null}
        </div>
      </div>

      {data.banner ? (
        <div className={styles.warningBanner} role="status">
          {data.banner.text}
        </div>
      ) : null}
      {notice ? <Banner tone="warning">{notice}</Banner> : null}

      <div className={styles.grid}>
        <div className={styles.stack}>
          <LogPanel record={data} onDone={invalidate} />
        </div>
        <div className={styles.stack}>
          <section className={styles.panel} aria-labelledby={`${data.id}-assigned`}>
            <h2 id={`${data.id}-assigned`} className={styles.eyebrow}>
              Zugewiesen
            </h2>
            <dl className={styles.rows}>
              <Row label="Extern">
                {data.assigned.external
                  ? [data.assigned.external.company, data.assigned.external.purpose]
                      .filter(Boolean)
                      .join(' · ')
                  : '—'}
              </Row>
              {data.assigned.contactPerson ? (
                <Row label="Ansprechpartner">{data.assigned.contactPerson}</Row>
              ) : null}
              <Row label="Intern">{data.assigned.internal ?? '—'}</Row>
              {data.assigned.deadline ? <Row label="Frist">{data.assigned.deadline}</Row> : null}
              {data.ticket ? (
                <Row label="Ticket">
                  <Link to={`/tickets/${data.ticket.id}`}>{data.ticket.numberLabel}</Link>
                </Row>
              ) : null}
            </dl>
            {actions.remind || (actions.call && data.assigned.phone) ? (
              <div className={styles.buttons}>
                {actions.remind ? (
                  <Button
                    variant="secondary"
                    size="small"
                    loading={reminding}
                    onClick={() => void remind(data)}
                  >
                    Erinnerung senden
                  </Button>
                ) : null}
                {actions.call && data.assigned.phone ? (
                  <a className={styles.callLink} href={`tel:${data.assigned.phone.replace(/[^\d+]/g, '')}`}>
                    Anrufen
                  </a>
                ) : null}
              </div>
            ) : null}
          </section>
          <CustomerTextPanel record={data} onDone={invalidate} />
        </div>
      </div>

      {dialog === 'assign-external' ? (
        <AssignExternalDialog record={data} onDone={invalidate} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'schedule' ? (
        <ScheduleDialog record={data} onDone={invalidate} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'extend' ? (
        <ExtendDialog record={data} onDone={invalidate} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'complete' ? (
        <CompleteDialog record={data} onDone={invalidate} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function Crumbs({ number }: { number: string | null }) {
  return (
    <nav aria-label="Brotkrumen" className={styles.crumbs}>
      <Link to="/tickets/after-sales" className={styles.crumbLink}>
        After-Sales
      </Link>
      {number ? (
        <>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{number}</span>
        </>
      ) : null}
    </nav>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.kv}>
      <dt className={styles.kvKey}>{label}</dt>
      <dd className={styles.kvValue}>{children}</dd>
    </div>
  );
}

function Photo({ fileId, label }: { fileId: string; label: string }) {
  const [failed, setFailed] = useState(false);
  const src = `/api/files/${fileId}`;
  return (
    <a className={styles.photo} href={src} target="_blank" rel="noreferrer" aria-label={`${label} öffnen`}>
      {failed ? (
        <ImageSlot alt={label} placeholder={label} height={140} chrome="frame" className={styles.photoSlot} />
      ) : (
        <img src={src} alt={label} onError={() => setFailed(true)} />
      )}
    </a>
  );
}

function LogPanel({ record, onDone }: { record: AfterSalesRecord; onDone: () => Promise<void> }) {
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    setPending(true);
    setError(null);
    try {
      await logCase(record.id, text.trim());
      await onDone();
      setText('');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={styles.panel} aria-labelledby={`${record.id}-log`}>
      <h2 id={`${record.id}-log`} className={styles.eyebrow}>
        Verlauf
      </h2>
      <dl className={styles.rows}>
        {record.log.map((entry, index) => (
          <Row key={`${entry.dateLabel}-${index}`} label={entry.dateLabel}>
            {entry.text}
          </Row>
        ))}
      </dl>
      {record.photos.length > 0 ? (
        <div className={styles.photos}>
          {record.photos.map((photo) => (
            <Photo key={photo.fileId} fileId={photo.fileId} label={photo.label} />
          ))}
        </div>
      ) : null}
      {record.status !== 'done' ? (
        <form className={styles.logForm} onSubmit={(event) => void submit(event)}>
          <TextField
            label="Eintrag hinzufügen"
            labelHidden
            className={styles.logField}
            placeholder="Eintrag hinzufügen, z. B. Rückmeldung des Handwerkers"
            value={text}
            maxLength={2000}
            onChange={(event) => setText(event.target.value)}
            error={error ?? undefined}
          />
          <Button type="submit" variant="secondary" loading={pending} disabled={!text.trim()}>
            Hinzufügen
          </Button>
        </form>
      ) : null}
    </section>
  );
}

function CustomerTextPanel({ record, onDone }: { record: AfterSalesRecord; onDone: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(record.customerStatusText ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await patchCase(record.id, { customerStatusText: text.trim() || null });
      await onDone();
      setEditing(false);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={styles.panel} aria-labelledby={`${record.id}-portal`}>
      <div className={styles.panelHead}>
        <h2 id={`${record.id}-portal`} className={styles.eyebrow}>
          Kunde sieht im Portal
        </h2>
        {!editing ? (
          <button
            type="button"
            className={styles.panelAction}
            onClick={() => {
              setText(record.customerStatusText ?? '');
              setEditing(true);
            }}
          >
            Bearbeiten
          </button>
        ) : null}
      </div>
      {editing ? (
        <form className={styles.quoteForm} onSubmit={(event) => void save(event)}>
          <Textarea
            label="Statustext für den Kunden"
            rows={3}
            maxLength={500}
            value={text}
            onChange={(event) => setText(event.target.value)}
            error={error ?? undefined}
          />
          <div className={styles.quoteButtons}>
            <Button variant="secondary" size="small" disabled={pending} onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
            <Button type="submit" size="small" loading={pending}>
              Speichern
            </Button>
          </div>
        </form>
      ) : record.customerStatusText ? (
        <div className={styles.quote}>„{record.customerStatusText}“</div>
      ) : (
        <div className={`${styles.quote} ${styles.quoteEmpty}`}>Noch kein Statustext.</div>
      )}
    </section>
  );
}

function CompleteDialog({
  record,
  onDone,
  onClose,
}: {
  record: AfterSalesRecord;
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setPending(true);
    setError(null);
    try {
      await completeCase(record.id);
      await onDone();
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={520}
      eyebrow={record.numberLabel}
      title="Als erledigt markieren?"
      subtitle={`„${record.title}“ wechselt zu „Erledigt“.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void confirm()}>
            Als erledigt markieren
          </Button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
    </Dialog>
  );
}
